import { Component, input, output, computed, inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialogModule, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatBadgeModule } from '@angular/material/badge';
import { LTFilterGroup, LTFilter, LTFilterColumn } from '../../models/lt-filter.models';
import { LTFilterService } from '../../services/lt-filter.service';
import { LTFilterDialogComponent } from '../lt-filter-dialog/lt-filter-dialog.component';

@Component({
  selector: 'app-lt-filter-button',
  imports: [
    CommonModule,
    MatButtonModule, 
    MatBadgeModule,
    MatDialogModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './lt-filter-button.component.html',
  styleUrl: './lt-filter-button.component.scss',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [LTFilterService],
})
export class LTFilterButtonComponent {
  private readonly dialog = inject(MatDialog);
  private readonly filterService = inject(LTFilterService);
  
  //#region Inputs-Outputs
  // Inputs
  readonly columns = input.required<LTFilterColumn[]>();
  readonly filter = input<LTFilter | null>();
  readonly disabled = input<boolean>(false);
  // inputs for dialog styling
  readonly width = input<string>('90vw');
  readonly height = input<string>('800px');
  readonly maxHeight = input<string>('90vh');

  // Outputs
  readonly filterChange = output<LTFilter | null>();
  //#endregion Inputs-Outputs
  
  // Private state
  private dialogRef: MatDialogRef<LTFilterDialogComponent> | null = null;

  //#region Computed properties
  readonly isActive = computed(() => this.filter() != null);

  readonly filterCount = computed(() => {
    const currentFilter = this.filter();
    if (!currentFilter) return 0;

    if (this.filterService.isFilterGroup(currentFilter)) {
      return this.countFiltersInGroup(currentFilter);
    }

    return 1;
  });

  readonly tooltipText = computed(() => {
    const count = this.filterCount();
    if (count === 0) return 'Add filters';
    if (count === 1) return '1 filter applied';
    return `${count} filters applied`;
  });
  //#endregion Computed properties

  openFilterDialog(): void {
    if (this.disabled()) return;

    this.dialogRef = this.dialog.open(LTFilterDialogComponent, {
      width: this.width(),
      height: this.height(),
      maxHeight: this.maxHeight(),
      disableClose: false,
      autoFocus: true,
      restoreFocus: true,
      data: {
        columns: this.columns(),
        filter: this.filter(),
      },
    });

    this.dialogRef.afterClosed().subscribe((result: LTFilter | null | undefined) => {
      if (result !== undefined) {
        this.filterChange.emit(result);
      }
      this.dialogRef = null;
    });
  }

  private countFiltersInGroup(filter: LTFilterGroup): number {
    return filter.filters.reduce((count: number, f) => {
      if (this.filterService.isFilterGroup(f)) {
        return count + this.countFiltersInGroup(f as LTFilterGroup);
      }
      return count + 1;
    }, 0);
  }
  
}
